import { useState } from "react";
import { useCorrigirNomeDoSistema } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { useAvisos } from "@/componentes/Avisos";
import { Campo } from "@/componentes/Campo";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { useSessao } from "@/sessao/useSessao";

/** O piso da rota: "ok" e "." são o jeito comum de derrotar uma exigência dessas. */
const MINIMO_DO_MOTIVO = 10;
const MAXIMO_DO_MOTIVO = 500;

/*
 * Corrige o nome da aplicação, com o motivo no próprio formulário (spec
 * `ciclo-de-vida-da-aplicacao`). O documento não tem rota: trocá-lo é trocar quem a
 * aplicação é para a API pública.
 *
 * Nome igual ao gravado para aqui: a rota responde `200` sem escrever nem auditar, e o aviso
 * "Nome corrigido" diria o contrário.
 */
export function CorrigirNome({
  sistemaID,
  nomeAtual,
  aoFechar,
}: {
  sistemaID: string;
  nomeAtual: string;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const corrigir = useCorrigirNomeDoSistema(cliente, sistemaID);

  const [nome, setNome] = useState(nomeAtual);
  const [motivo, setMotivo] = useState("");
  const [erroDoNome, setErroDoNome] = useState<string | undefined>();
  const [erroDoMotivo, setErroDoMotivo] = useState<string | undefined>();

  function recusaDoNome(nomeLimpo: string): string | undefined {
    if (nomeLimpo === "") return "Informe o nome.";
    return nomeLimpo === nomeAtual ? "O nome é o mesmo." : undefined;
  }

  function enviar() {
    const nomeLimpo = nome.trim();
    const motivoLimpo = motivo.trim();
    const recusa = recusaDoNome(nomeLimpo);
    const motivoCurto = motivoLimpo.length < MINIMO_DO_MOTIVO;

    setErroDoNome(recusa);
    setErroDoMotivo(motivoCurto ? `Mínimo de ${MINIMO_DO_MOTIVO} caracteres.` : undefined);
    if (recusa !== undefined || motivoCurto) return;

    corrigir.mutate(
      { nome: nomeLimpo, motivo: motivoLimpo },
      {
        onSuccess: (gravado) => {
          avisos.feito("Nome corrigido", gravado.nome);
          aoFechar();
        },
      },
    );
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Corrigir nome"
      aoEnviar={enviar}
      enviando={corrigir.isPending}
      erro={corrigir.error ? textoDoErro(corrigir.error) : undefined}
      rotuloDeEnvio="Corrigir nome"
    >
      <Campo
        rotulo="Nome"
        value={nome}
        onChange={(evento) => setNome(evento.target.value)}
        erro={erroDoNome}
        autoFocus
      />

      <Campo
        rotulo="Motivo"
        value={motivo}
        onChange={(evento) => setMotivo(evento.target.value)}
        erro={erroDoMotivo}
        maxLength={MAXIMO_DO_MOTIVO}
        autoComplete="off"
        dica={`Vai para a auditoria. Mínimo de ${MINIMO_DO_MOTIVO} caracteres.`}
      />
    </ModalDeCadastro>
  );
}
